import { CurrencyAmount, Percent } from '@kodiak-finance/sdk-core'
import { NonfungiblePositionManager } from '@kodiak-finance/v3-sdk'
import { useQueryClient } from '@tanstack/react-query'
import RangeBadge from 'components/Badge/RangeBadge'
import { ButtonConfirmed, ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import FormattedCurrencyAmount from 'components/FormattedCurrencyAmount'
import Loader from 'components/Loader'
import { AddRemoveTabs } from 'components/NavigationTabs'
import { AutoRow, Break, RowBetween, RowFixed } from 'components/Row'
import Slider from 'components/Slider'
import Toggle from 'components/Toggle'
import { NONFUNGIBLE_POSITION_MANAGER_ADDRESSES } from 'constants/addresses'
import { v3QueryKeys } from 'hooks/subgraph/v3'
import { useTokens } from 'hooks/Tokens'
import { useChainAwareNavigate } from 'hooks/useChainAwareNavigate'
import { useConnection } from 'hooks/useConnection'
import useDebouncedChangeHandler from 'hooks/useDebouncedChangeHandler'
import useTheme from 'hooks/useTheme'
import { useSendTransaction } from 'hooks/useTransaction'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { useV3PositionFromTokenId } from 'hooks/useV3Positions'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import useNativeCurrency from 'lib/hooks/useNativeCurrency'
import { PageWrapper } from 'pages/AddLiquidity/styled'
import { useCallback, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { Text } from 'rebass'
import { useBurnV3ActionHandlers, useBurnV3State, useDerivedV3BurnInfo } from 'state/burn/v3/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { useUserSlippageToleranceWithDefault } from 'state/user/hooks'
import { ThemedText } from 'theme'
import { Hex } from 'viem'

import TransactionConfirmationModal, {
  ConfirmationModalContent,
  TransactionErrorContent,
} from '../../components/TransactionConfirmationModal'
import { useNativeCurrencyPreference } from '../../hooks/useNativeCurrencyPreference'
import { TransactionType } from '../../state/transactions/types'
import { calculateGasMargin } from '../../utils/calculateGasMargin'
import { currencyId } from '../../utils/currencyId'
import { ResponsiveHeaderText, SmallMaxButton, Wrapper } from './styled'

const DEFAULT_REMOVE_V3_LIQUIDITY_SLIPPAGE_TOLERANCE = new Percent(5, 100)

interface TransactionState {
  attemptingTxn: boolean
  errorMessage: string | undefined
  txHash: string | undefined
  transactionWaiting?: boolean
}

// redirect invalid tokenIds
export default function RemoveLiquidityV3() {
  const { tokenId } = useParams<{ tokenId: string }>()
  const location = useLocation()
  const parsedTokenId = useMemo(() => {
    if (!tokenId) return null
    try {
      // Check if it's a non-empty string that can be safely converted to a non-zero BigInt
      const idBigInt = BigInt(tokenId)
      if (idBigInt === 0n) {
        return null // Treat 0 as invalid
      }
      return tokenId // If valid, return the original string
    } catch {
      return null // Catch non-numeric or invalid string formats
    }
  }, [tokenId])

  if (parsedTokenId === null) {
    return <Navigate to={{ ...location, pathname: '/liquidity/v3' }} replace />
  }

  return <Remove tokenId={parsedTokenId} />
}

function Remove({ tokenId }: { tokenId: string }) {
  const navigate = useChainAwareNavigate()
  const { position, loading: positionLoading } = useV3PositionFromTokenId(tokenId)
  const isCheckingExisting = Boolean(tokenId) && positionLoading

  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [token0, token1] = useTokens([position?.token0, position?.token1])

  const theme = useTheme()
  const { account, chainId, wagmiPublicClient } = useConnection()
  const { sendTransactionAsync } = useSendTransaction()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()

  // flag for receiving WETH
  const [receiveWETH, setReceiveWETH] = useState(false)
  const { shouldShowNativeCurrencyToggle } = useNativeCurrencyPreference(receiveWETH)
  const nativeCurrency = useNativeCurrency()
  const nativeWrappedSymbol = nativeCurrency.wrapped.symbol

  // burn state
  const { percent } = useBurnV3State()
  const {
    position: positionSDK,
    liquidityPercentage,
    liquidityValue0,
    liquidityValue1,
    feeValue0,
    feeValue1,
    outOfRange,
    error,
  } = useDerivedV3BurnInfo(position, receiveWETH)

  const { onPercentSelect } = useBurnV3ActionHandlers()

  const removed = BigInt(position?.liquidity ?? '0') === 0n

  // boilerplate for the slider
  const [percentForSlider, onPercentSelectForSlider] = useDebouncedChangeHandler(percent, onPercentSelect)

  const deadline = useTransactionDeadline() // custom from users settings
  const allowedSlippage = useUserSlippageToleranceWithDefault(DEFAULT_REMOVE_V3_LIQUIDITY_SLIPPAGE_TOLERANCE) // custom from users

  const [transactionState, setTransactionState] = useState<TransactionState>({
    attemptingTxn: false,
    errorMessage: undefined,
    txHash: undefined,
  })

  const [showConfirm, setShowConfirm] = useState(false)
  const addTransaction = useTransactionAdder()
  const positionManagerAddress = NONFUNGIBLE_POSITION_MANAGER_ADDRESSES[chainId]

  const burn = useCallback(async () => {
    // Set attempting state immediately to show loading
    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })

    try {
      if (
        !positionManagerAddress ||
        !liquidityValue0 ||
        !liquidityValue1 ||
        !deadline ||
        !account ||
        !chainId ||
        !positionSDK ||
        !liquidityPercentage ||
        !wagmiPublicClient
      ) {
        throw new Error('Missing dependencies for transaction')
      }

      const { calldata, value } = NonfungiblePositionManager.removeCallParameters(positionSDK, {
        tokenId, // tokenId is already a string
        liquidityPercentage,
        slippageTolerance: allowedSlippage,
        deadline: deadline.toString(),
        collectOptions: {
          expectedCurrencyOwed0: feeValue0 ?? CurrencyAmount.fromRawAmount(liquidityValue0.currency, 0),
          expectedCurrencyOwed1: feeValue1 ?? CurrencyAmount.fromRawAmount(liquidityValue1.currency, 0),
          recipient: account,
        },
      })

      const txn = {
        to: positionManagerAddress,
        data: calldata as Hex,
        value: BigInt(value),
        account,
      }

      // Estimate gas using wagmi public client
      const estimate = await wagmiPublicClient.estimateGas(txn)

      // Execute the transaction using wagmi
      const hash = await sendTransactionAsync({
        to: txn.to,
        data: txn.data,
        gas: calculateGasMargin(estimate as bigint) as bigint,
        value: txn.value,
      })

      // Execute the transaction
      const response = { hash }

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: response.hash,
        transactionWaiting: true,
      })

      addTransaction(response, {
        type: TransactionType.REMOVE_LIQUIDITY_V3,
        baseCurrencyId: currencyId(liquidityValue0.currency),
        quoteCurrencyId: currencyId(liquidityValue1.currency),
        expectedAmountBaseRaw: liquidityValue0.quotient.toString(),
        expectedAmountQuoteRaw: liquidityValue1.quotient.toString(),
      })

      const receipt = await waitForTransactionReceipt({ hash })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: receipt.transactionHash,
        transactionWaiting: false,
      })

      if (account) {
        await queryClient.invalidateQueries({ queryKey: ['v3'] })
        await queryClient.invalidateQueries({
          queryKey: v3QueryKeys.positionById(chainId, tokenId.toLowerCase()),
        })
      }
    } catch (error: any) {
      setTransactionState({
        attemptingTxn: false,
        errorMessage: error?.message || error?.reason || 'Remove liquidity failed',
        txHash: undefined,
        transactionWaiting: false,
      })

      // Log the error for debugging
      console.error('Remove V3 liquidity error:', error)
    }
  }, [
    positionManagerAddress,
    liquidityValue0,
    liquidityValue1,
    deadline,
    account,
    chainId,
    feeValue0,
    feeValue1,
    positionSDK,
    liquidityPercentage,
    wagmiPublicClient,
    tokenId,
    allowedSlippage,
    addTransaction,
    dispatch,
    sendTransactionAsync,
    waitForTransactionReceipt,
  ])

  const handleDismissConfirmation = useCallback(() => {
    setShowConfirm(false)
    // if there was a tx hash, we want to clear the input
    if (transactionState.txHash) {
      onPercentSelectForSlider(0)
    }
    setTransactionState({
      attemptingTxn: false,
      errorMessage: undefined,
      txHash: undefined,
    })

    navigate('/liquidity/v3')
  }, [navigate, onPercentSelectForSlider, transactionState.txHash])

  const confirmationContent = useCallback(() => {
    if (transactionState.errorMessage) {
      return <TransactionErrorContent onDismiss={handleDismissConfirmation} message={transactionState.errorMessage} />
    }
    if (transactionState.txHash) {
      return (
        <ConfirmationModalContent
          title={<>Remove Liquidity</>}
          onDismiss={handleDismissConfirmation}
          topContent={() => (
            <div>
              <div style={{ textAlign: 'center', padding: '1rem' }}>
                <div style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>Transaction Submitted</div>
                <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>
                  Removing {liquidityValue0?.toSignificant(6)} {liquidityValue0?.currency?.symbol} and{' '}
                  {liquidityValue1?.toSignificant(6)} {liquidityValue1?.currency?.symbol}
                </div>
              </div>
            </div>
          )}
          bottomContent={() => null}
        />
      )
    }
    return (
      <ConfirmationModalContent
        title={<>Remove Liquidity</>}
        onDismiss={handleDismissConfirmation}
        topContent={modalHeader}
      />
    )
  }, [
    transactionState.errorMessage,
    transactionState.txHash,
    handleDismissConfirmation,
    liquidityValue0,
    liquidityValue1,
  ])

  const pendingText = (
    <>
      Removing {liquidityValue0?.toSignificant(6)} {liquidityValue0?.currency?.symbol} and{' '}
      {liquidityValue1?.toSignificant(6)} {liquidityValue1?.currency?.symbol}
    </>
  )

  function modalHeader() {
    return (
      <AutoColumn gap="sm" style={{ padding: '16px' }}>
        <RowBetween align="flex-end">
          <Text fontSize={16} fontWeight={500}>
            <>Pooled {liquidityValue0?.currency?.symbol}:</>
          </Text>
          <RowFixed>
            <Text fontSize={16} fontWeight={500} marginLeft="6px">
              {liquidityValue0 && <FormattedCurrencyAmount currencyAmount={liquidityValue0} />}
            </Text>
            <CurrencyLogo size="20px" className="ml-2" currency={liquidityValue0?.currency} />
          </RowFixed>
        </RowBetween>
        <RowBetween align="flex-end">
          <Text fontSize={16} fontWeight={500}>
            <>Pooled {liquidityValue1?.currency?.symbol}:</>
          </Text>
          <RowFixed>
            <Text fontSize={16} fontWeight={500} marginLeft="6px">
              {liquidityValue1 && <FormattedCurrencyAmount currencyAmount={liquidityValue1} />}
            </Text>
            <CurrencyLogo size="20px" className="ml-2" currency={liquidityValue1?.currency} />
          </RowFixed>
        </RowBetween>
        {feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0) ? (
          <>
            <ThemedText.DeprecatedItalic
              fontSize={12}
              color={theme.deprecated_text2}
              textAlign="left"
              padding="8px 0 0 0"
            >
              <>You will also collect fees earned from this position.</>
            </ThemedText.DeprecatedItalic>
            <RowBetween>
              <Text fontSize={16} fontWeight={500}>
                <>{feeValue0?.currency?.symbol} Fees Earned:</>
              </Text>
              <RowFixed>
                <Text fontSize={16} fontWeight={500} marginLeft="6px">
                  {feeValue0 && <FormattedCurrencyAmount currencyAmount={feeValue0} />}
                </Text>
                <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={feeValue0?.currency} />
              </RowFixed>
            </RowBetween>
            <RowBetween>
              <Text fontSize={16} fontWeight={500}>
                <>{feeValue1?.currency?.symbol} Fees Earned:</>
              </Text>
              <RowFixed>
                <Text fontSize={16} fontWeight={500} marginLeft="6px">
                  {feeValue1 && <FormattedCurrencyAmount currencyAmount={feeValue1} />}
                </Text>
                <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={feeValue1?.currency} />
              </RowFixed>
            </RowBetween>
          </>
        ) : null}
        <ButtonPrimary mt="16px" onClick={burn}>
          <>Remove</>
        </ButtonPrimary>
      </AutoColumn>
    )
  }

  const showCollectAsWeth = shouldShowNativeCurrencyToggle(liquidityValue0?.currency, liquidityValue1?.currency)

  if (isCheckingExisting) {
    return (
      <AutoColumn>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
          <Loader size="40px" />
        </div>
      </AutoColumn>
    )
  }

  return (
    <AutoColumn style={{ width: '100%' }}>
      <TransactionConfirmationModal
        isOpen={showConfirm}
        onDismiss={handleDismissConfirmation}
        attemptingTxn={transactionState.attemptingTxn}
        hash={transactionState.txHash ?? ''}
        transactionWaiting={transactionState.transactionWaiting}
        content={confirmationContent}
        pendingText={pendingText}
      />
      <PageWrapper wide={false}>
        <AddRemoveTabs
          creating={false}
          adding={false}
          positionID={undefined}
          defaultSlippage={DEFAULT_REMOVE_V3_LIQUIDITY_SLIPPAGE_TOLERANCE}
        />
        <Wrapper>
          <AutoColumn gap="lg">
            <RowBetween>
              <RowFixed>
                <DoubleCurrencyLogo currency0={token0} currency1={token1} size={20} margin={true} />
                <ThemedText.DeprecatedLabel
                  ml="10px"
                  fontSize="20px"
                >{`${token0?.symbol}/${token1?.symbol}`}</ThemedText.DeprecatedLabel>
              </RowFixed>
              <RangeBadge removed={removed} inRange={!outOfRange} />
            </RowBetween>
            <LightCard>
              <AutoColumn gap="md">
                <ThemedText.DeprecatedMain fontWeight={400}>
                  <>Amount</>
                </ThemedText.DeprecatedMain>
                <RowBetween>
                  <ResponsiveHeaderText>
                    <>{percentForSlider}%</>
                  </ResponsiveHeaderText>
                  <AutoRow gap="4px" justify="flex-end">
                    <SmallMaxButton onClick={() => onPercentSelect(25)} width="20%">
                      <>25%</>
                    </SmallMaxButton>
                    <SmallMaxButton onClick={() => onPercentSelect(50)} width="20%">
                      <>50%</>
                    </SmallMaxButton>
                    <SmallMaxButton onClick={() => onPercentSelect(75)} width="20%">
                      <>75%</>
                    </SmallMaxButton>
                    <SmallMaxButton onClick={() => onPercentSelect(100)} width="20%">
                      <>Max</>
                    </SmallMaxButton>
                  </AutoRow>
                </RowBetween>
                <Slider value={percentForSlider} onChange={onPercentSelectForSlider} />
              </AutoColumn>
            </LightCard>
            <LightCard>
              <AutoColumn gap="md">
                <RowBetween>
                  <Text fontSize={16} fontWeight={500}>
                    <>Pooled {liquidityValue0?.currency?.symbol}:</>
                  </Text>
                  <RowFixed>
                    <Text fontSize={16} fontWeight={500} marginLeft="6px">
                      {liquidityValue0 && <FormattedCurrencyAmount currencyAmount={liquidityValue0} />}
                    </Text>
                    <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={liquidityValue0?.currency} />
                  </RowFixed>
                </RowBetween>
                <RowBetween>
                  <Text fontSize={16} fontWeight={500}>
                    <>Pooled {liquidityValue1?.currency?.symbol}:</>
                  </Text>
                  <RowFixed>
                    <Text fontSize={16} fontWeight={500} marginLeft="6px">
                      {liquidityValue1 && <FormattedCurrencyAmount currencyAmount={liquidityValue1} />}
                    </Text>
                    <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={liquidityValue1?.currency} />
                  </RowFixed>
                </RowBetween>
                {feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0) ? (
                  <>
                    <Break />
                    <RowBetween>
                      <Text fontSize={16} fontWeight={500}>
                        <>{feeValue0?.currency?.symbol} Fees Earned:</>
                      </Text>
                      <RowFixed>
                        <Text fontSize={16} fontWeight={500} marginLeft="6px">
                          {feeValue0 && <FormattedCurrencyAmount currencyAmount={feeValue0} />}
                        </Text>
                        <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={feeValue0?.currency} />
                      </RowFixed>
                    </RowBetween>
                    <RowBetween>
                      <Text fontSize={16} fontWeight={500}>
                        <>{feeValue1?.currency?.symbol} Fees Earned:</>
                      </Text>
                      <RowFixed>
                        <Text fontSize={16} fontWeight={500} marginLeft="6px">
                          {feeValue1 && <FormattedCurrencyAmount currencyAmount={feeValue1} />}
                        </Text>
                        <CurrencyLogo size="20px" style={{ marginLeft: '8px' }} currency={feeValue1?.currency} />
                      </RowFixed>
                    </RowBetween>
                  </>
                ) : null}
              </AutoColumn>
            </LightCard>

            {showCollectAsWeth && (
              <RowBetween>
                <ThemedText.DeprecatedMain>
                  <>Collect as {nativeWrappedSymbol}</>
                </ThemedText.DeprecatedMain>
                <Toggle
                  id="receive-as-weth"
                  isActive={receiveWETH}
                  useAlternateBackground
                  toggle={() => setReceiveWETH((receiveWETH) => !receiveWETH)}
                />
              </RowBetween>
            )}

            <div style={{ display: 'flex' }}>
              <AutoColumn gap="12px" style={{ flex: '1' }}>
                <ButtonConfirmed
                  confirmed={false}
                  disabled={removed || percent === 0 || !liquidityValue0}
                  onClick={() => setShowConfirm(true)}
                >
                  {removed ? <>Closed</> : error ?? <>Remove</>}
                </ButtonConfirmed>
              </AutoColumn>
            </div>
          </AutoColumn>
        </Wrapper>
      </PageWrapper>
    </AutoColumn>
  )
}
